import React from 'react'

import { cn } from './cn'
import { Icon } from './Icon'
import type { IconName } from './Icon'
import { Label } from './Label'
import type { LabelProps } from './Label'

// DOM mirrors Semantic UI React's Input (elements/Input/Input.js) for the props the app uses.
// The wrapper's class words come first, in SUIR's order (ui, size, disabled, error, fluid,
// action, icon, labeled, input), as marker classes, joined outside cn() as Button does.
//
// HTML input props (SUIR's htmlInputAttrs and events, plus aria-* and role) go to the inner
// `<input>`, merged with the `input` prop, and the rest go to the wrapper. Children render as
// given, and an `<input>` child gets the HTML input props as SUIR's clone does. Only `<input>`
// children get them. The order of the attributes matches SUIR's, because the DOM parity test
// compares innerHTML.
//
// The styles are copied from Semantic UI 2.4.2 input.css for these variants. Rules on the
// inner `input`, `.icon` and `.button`/`.buttons`/`.label` children are Tailwind arbitrary
// variants on the root, so they keep Semantic's specificity. `:not([class*="left action"])`
// and `corner` are dropped, because lib/ui never emits `left action`, `corner` or `right`
// labels, so those selectors always match. Left-action rules are dropped for the same reason.
// The icon's svg is centred by `.ui.icon.input > i.icon > svg` in globals.css (MAD-118).
// `loading`, `focus`, `inverted`, `transparent`, `as`, `tabIndex`, `ref` and the `mini`,
// `small`, `big`, `huge` and `massive` sizes are not implemented, because no call site uses
// them.

export type InputElementProps = React.InputHTMLAttributes<HTMLInputElement> & {
  'data-testid'?: string
}

export type InputLabel = string | number | LabelProps | React.ReactElement

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'size' | 'type'
> & {
  action?: boolean | React.ReactElement
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  error?: boolean
  fluid?: boolean
  icon?: IconName
  iconPosition?: 'left'
  // Props for the inner `<input>`, merged over the HTML input props.
  input?: InputElementProps
  label?: InputLabel
  // SUIR calls onChange(event, { ...props, value }) with the new value.
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, data: InputData) => void
  size?: 'large'
  type?: string
}

export type InputData = InputProps & { value: string }

// SUIR's htmlInputAttrs and htmlInputEvents, minus `onChange`, which Input handles itself.
const HTML_INPUT_PROPS = new Set<string>([
  'selected',
  'defaultValue',
  'defaultChecked',
  'accept',
  'autoCapitalize',
  'autoComplete',
  'autoCorrect',
  'autoFocus',
  'checked',
  'disabled',
  'enterKeyHint',
  'form',
  'id',
  'inputMode',
  'lang',
  'list',
  'max',
  'maxLength',
  'min',
  'minLength',
  'multiple',
  'name',
  'pattern',
  'placeholder',
  'readOnly',
  'required',
  'step',
  'title',
  'type',
  'value',
  'onKeyDown',
  'onKeyPress',
  'onKeyUp',
  'onFocus',
  'onBlur',
  'onInput',
  'onClick',
  'onContextMenu',
  'onDrag',
  'onDragEnd',
  'onDragEnter',
  'onDragExit',
  'onDragLeave',
  'onDragOver',
  'onDragStart',
  'onDrop',
  'onMouseDown',
  'onMouseEnter',
  'onMouseLeave',
  'onMouseMove',
  'onMouseOut',
  'onMouseOver',
  'onMouseUp',
  'onSelect',
  'onTouchCancel',
  'onTouchEnd',
  'onTouchMove',
  'onTouchStart',
])

// `.ui.input` (font, flex wrapper) and `.ui.input > input` (padding, border, radius).
const INPUT_BASE = [
  '[&.ui]:relative [&.ui]:inline-flex [&.ui]:[font-size:1em] [&.ui]:[font-weight:normal]',
  '[&.ui]:not-italic [&.ui]:text-[rgba(0,0,0,0.87)]',
  '[&.ui>input]:m-0 [&.ui>input]:max-w-full [&.ui>input]:[flex:1_0_auto] [&.ui>input]:[outline:none]',
  '[&.ui>input]:text-left [&.ui>input]:leading-[1.21428571em] [&.ui>input]:[font-family:var(--ui-font-sans)]',
  '[&.ui>input]:py-[0.67857143em] [&.ui>input]:px-[1em] [&.ui>input]:bg-[#FFFFFF]',
  '[&.ui>input]:border-[1px] [&.ui>input]:border-solid [&.ui>input]:border-[rgba(34,36,38,0.15)]',
  '[&.ui>input]:text-[rgba(0,0,0,0.87)] [&.ui>input]:rounded-[0.28571429rem]',
  '[&.ui>input]:[transition:border-color_0.1s_ease,box-shadow_0.1s_ease] [&.ui>input]:[box-shadow:none]',
  // Placeholder.
  '[&.ui>input]:placeholder:[color:rgba(191,191,191,0.87)]',
  // Focus. The error focus rules below are more specific, so they win as Semantic's do.
  '[&.ui>input:focus]:border-[#85B7D9] [&.ui>input:focus]:bg-[#FFFFFF]',
  '[&.ui>input:focus]:text-[rgba(0,0,0,0.8)] [&.ui>input:focus]:[box-shadow:none]',
  '[&.ui>input:focus]:placeholder:[color:rgba(115,115,115,0.87)]',
].join(' ')

// `.ui.disabled.input`, `.ui.input:not(.disabled) input[disabled]`.
const DISABLED = [
  '[&.ui.disabled]:opacity-[0.45] [&.ui.input:not(.disabled)_input[disabled]]:opacity-[0.45]',
  '[&.ui.disabled>input]:pointer-events-none [&.ui.input:not(.disabled)_input[disabled]]:pointer-events-none',
].join(' ')

// `.ui.input.error > input` and its placeholders, and the focused error rules.
const ERROR = [
  '[&.ui.error>input]:bg-[#FFF6F6] [&.ui.error>input]:border-[#E0B4B4] [&.ui.error>input]:text-[#9F3A38]',
  '[&.ui.error>input]:[box-shadow:none] [&.ui.error>input]:placeholder:[color:#e7bdbc]',
  '[&.ui.error>input:focus]:bg-[#FFF6F6] [&.ui.error>input:focus]:border-[#E0B4B4]',
  '[&.ui.error>input:focus]:text-[#9F3A38] [&.ui.error>input:focus]:placeholder:[color:#da9796]',
].join(' ')

// `.ui.fluid.input`.
const FLUID = '[&.ui.fluid]:flex [&.ui.fluid>input]:!w-0'

// `.ui.large.input`.
const LARGE = '[&.ui.large]:[font-size:1.14285714em]'

// `.ui.icon.input > i.icon` (the icon box) and the input padding for it. The svg inside the box
// is centred in globals.css.
const ICON = [
  '[&.ui.icon.input>i.icon]:cursor-default [&.ui.icon.input>i.icon]:absolute',
  '[&.ui.icon.input>i.icon]:leading-[1] [&.ui.icon.input>i.icon]:text-center',
  '[&.ui.icon.input>i.icon]:top-0 [&.ui.icon.input>i.icon]:right-0 [&.ui.icon.input>i.icon]:m-0',
  '[&.ui.icon.input>i.icon]:h-full [&.ui.icon.input>i.icon]:w-[2.67142857em]',
  '[&.ui.icon.input>i.icon]:opacity-50 [&.ui.icon.input>i.icon]:rounded-[0em_0.28571429rem_0.28571429rem_0em]',
  '[&.ui.icon.input>i.icon]:[transition:opacity_0.3s_ease] [&.ui.icon.input>i.icon]:pointer-events-none',
  '[&.ui.icon.input>input]:!pr-[2.67142857em]',
  '[&.ui.icon.input>input:focus~i.icon]:opacity-100',
].join(' ')

// `.ui.left.icon.input`: the icon moves to the left (the `left icon` marker word).
const LEFT_ICON = [
  '[&.ui.left.icon.input>i.icon]:right-auto [&.ui.left.icon.input>i.icon]:left-[1px]',
  '[&.ui.left.icon.input>i.icon]:rounded-[0.28571429rem_0em_0em_0.28571429rem]',
  '[&.ui.left.icon.input>input]:!pl-[2.67142857em] [&.ui.left.icon.input>input]:!pr-[1em]',
].join(' ')

// `.ui.labeled.input > .label` (the label on the left), and the input's left radii.
const LABELED = [
  '[&.ui.labeled.input>.label]:[flex:0_0_auto] [&.ui.labeled.input>.label]:m-0',
  '[&.ui.labeled.input>.label]:[font-size:1em] [&.ui.labeled.input>.label]:py-[0.78571429em]',
  '[&.ui.labeled.input_.label:first-child]:![border-top-right-radius:0px]',
  '[&.ui.labeled.input_.label:first-child]:![border-bottom-right-radius:0px]',
  '[&.ui.labeled.input_.label:first-child+input]:![border-top-left-radius:0px]',
  '[&.ui.labeled.input_.label:first-child+input]:![border-bottom-left-radius:0px]',
  '[&.ui.labeled.input_.label:first-child+input]:![border-left-color:transparent]',
  '[&.ui.labeled.input_.label:first-child+input:focus]:![border-left-color:#85B7D9]',
].join(' ')

// `.ui.action.input` with a button on the right (the only side lib/ui renders). Always applied,
// because the rules key on the `action` word, and PhoneInput sets that word through `className`
// in children mode rather than through the `action` prop, as Semantic allows.
const ACTION = [
  '[&.ui.action.input>.button]:[display:flex] [&.ui.action.input>.button]:items-center',
  '[&.ui.action.input>.button]:[flex:0_0_auto]',
  '[&.ui.action.input>.buttons]:[display:flex] [&.ui.action.input>.buttons]:items-center',
  '[&.ui.action.input>.buttons]:[flex:0_0_auto]',
  '[&.ui.action.input>.button]:pt-[0.78571429em] [&.ui.action.input>.button]:pb-[0.78571429em]',
  '[&.ui.action.input>.button]:m-0',
  '[&.ui.action.input>.buttons>.button]:pt-[0.78571429em] [&.ui.action.input>.buttons>.button]:pb-[0.78571429em]',
  '[&.ui.action.input>.buttons>.button]:m-0',
  '[&.ui.action.input>input]:![border-top-right-radius:0px]',
  '[&.ui.action.input>input]:![border-bottom-right-radius:0px]',
  '[&.ui.action.input>input]:![border-right-color:transparent]',
  '[&.ui.action.input>.button:not(:first-child)]:rounded-none',
  '[&.ui.action.input>.buttons:not(:first-child)>.button]:rounded-none',
  '[&.ui.action.input>.button:last-child]:[border-radius:0px_0.28571429rem_0.28571429rem_0px]',
  '[&.ui.action.input>.buttons:last-child>.button]:[border-radius:0px_0.28571429rem_0.28571429rem_0px]',
  '[&.ui.action.input>input:focus]:![border-right-color:#85B7D9]',
].join(' ')

function isNil(value: unknown): value is null | undefined {
  return value === null || value === undefined
}

function renderLabel(label: InputLabel | undefined): React.ReactNode {
  if (isNil(label)) return null
  if (React.isValidElement(label)) return label
  if (typeof label === 'object') return <Label {...label} />
  return <Label content={label} />
}

export function Input(props: InputProps) {
  const {
    action,
    children,
    className,
    disabled,
    error,
    fluid,
    icon,
    iconPosition,
    input,
    label,
    onChange,
    size,
    type = 'text',
    ...unhandled
  } = props

  // SUIR's partitionHTMLProps: HTML input props (and aria-*, role) go to the inner input.
  const htmlInputProps: Record<string, unknown> = {}
  const wrapperProps: Record<string, unknown> = {}
  for (const [prop, value] of Object.entries(unhandled)) {
    const isHtmlInputProp =
      HTML_INPUT_PROPS.has(prop) || /^aria-/.test(prop) || prop === 'role'
    if (isHtmlInputProp) htmlInputProps[prop] = value
    else wrapperProps[prop] = value
  }

  const inputProps = {
    ...htmlInputProps,
    disabled,
    type,
    tabIndex: disabled ? -1 : undefined,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange?.(e, { ...props, value: e.target.value }),
  } as React.InputHTMLAttributes<HTMLInputElement>

  // SUIR's class words, in its order. `icon` alone gives `icon`; with iconPosition it is
  // `left icon`. A truthy `action` (boolean or element) gives `action`.
  const markers = [
    'ui',
    size,
    disabled && 'disabled',
    error && 'error',
    fluid && 'fluid',
    action && 'action',
    iconPosition ? 'left icon' : icon && 'icon',
    label && 'labeled',
    'input',
  ]
    .filter(Boolean)
    .join(' ')

  const rules = cn(
    INPUT_BASE,
    size === 'large' && LARGE,
    disabled && DISABLED,
    error && ERROR,
    fluid && FLUID,
    (icon || iconPosition) && ICON,
    iconPosition === 'left' && LEFT_ICON,
    label && LABELED,
    ACTION,
    className,
  )
  const wrapperClass = `${markers} ${rules}`

  // With children, SUIR renders them as given and clones the `<input>` child with the HTML
  // input props. Icon, label and action shorthands are not rendered.
  if (!isNil(children)) {
    const elements = React.Children.toArray(children).map(child =>
      React.isValidElement(child) && child.type === 'input'
        ? React.cloneElement(
            child as React.ReactElement<Record<string, unknown>>,
            {
              ...inputProps,
              ...(child.props as Record<string, unknown>),
            },
          )
        : child,
    )
    return (
      <div {...wrapperProps} className={wrapperClass}>
        {elements}
      </div>
    )
  }

  const actionElement = React.isValidElement(action) ? action : null
  const iconElement = icon ? <Icon name={icon} /> : null

  return (
    <div {...wrapperProps} className={wrapperClass}>
      {renderLabel(label)}
      <input {...inputProps} {...input} />
      {iconElement}
      {actionElement}
    </div>
  )
}
